import { Component, Host, Prop, Element, h, State, Watch } from "@stencil/core";

/**
 * @slot video - insert the video HTML element
 * @slot link - insert a the call to action (link) that leads to the video page
 * @slot - insert the content of the teaser description, linke title and duration
 */

@Component({
    tag: "ux-iws-teaser-video-search",
    styleUrl: "teaser-video-search.scss",
    shadow: false,
})
export class TeaserVideo {
    private baseClass = "ux-iws-teaser-video-search";
    private toVideoUrl: string;

    private video: HTMLVideoElement;

    @Element() element: HTMLUxIwsTeaserVideoSearchElement;

    /** the text of the play again button */
    @Prop() playAgainText: string;
    /** the title of the video to assign as a title attribute to the link */
    @Prop() videoTitle: string;

    @State() showOverlay: boolean = false;
    @State() isSafari: boolean = false;
    @State() videoPaused: boolean = true;
    @State() showControls: boolean = false;

    private playVideo = () => {
        if (!this.showControls) this.showControls = true;
        this.videoPaused = false;
        this.video.play();
    };

    private handlePause = () => {
        this.showOverlay = true;
    };

    private handleEnd = () => {
        this.showOverlay = true;
    };

    private handlePlay = () => {
        if (this.showOverlay === true) this.showOverlay = false;
    };

    @Watch("showControls")
    handleVideoControls() {
        if (!this.video) return;
        this.video.setAttribute("controls", "");
    }

    connectedCallback() {
        this.video = this.element.querySelector("video");
        if (!this.video) return;
        if (this.video.hasAttribute("controls")) {
            this.video.removeAttribute("controls");
        }
        this.video.addEventListener("play", this.handlePlay);
        this.video.addEventListener("ended", this.handleEnd);
        this.video.addEventListener("pause", this.handlePause);
        const link = this.element.querySelector("a");
        const url = link?.getAttribute("href");
        if (!url) return;
        this.toVideoUrl = url;
    }

    disconnectedCallback() {
        if (!this.video) return;
        this.video.removeEventListener("play", this.handlePlay);
        this.video.removeEventListener("ended", this.handleEnd);
        this.video.removeEventListener("pause", this.handlePause);
    }

    render() {
        const { baseClass, showOverlay, videoPaused } = this;
        return (
            <Host class={baseClass}>
                <div class={`${baseClass}__video-wrapper`}>
                    <slot name="video" />
                    <div
                        class={{
                            [`${baseClass}__play-button`]: true,
                            [`${baseClass}__play-button--inactive`]:
                                !videoPaused,
                        }}
                        onClick={this.playVideo}
                    >
                        <ux-icon
                            class={`${baseClass}__play-icon`}
                            name="play-filled"
                        ></ux-icon>
                    </div>
                    <div
                        class={{
                            [`${baseClass}__video-overlay`]: true,
                            [`${baseClass}__video-overlay--active`]:
                                showOverlay,
                        }}
                    >
                        <span onClick={this.playVideo} class="ux-button">
                            {this.playAgainText}
                        </span>
                        <slot name="link" />
                    </div>
                </div>
                <ux-iws-link-wrapper class={`${baseClass}__info-wrapper`}>
                    <div>
                        <slot />
                    </div>
                    <div class={`${baseClass}__cta`}>
                        <a
                            href={this.toVideoUrl}
                            class="ux-button ux-button--icon-only"
                            title={this.videoTitle}
                            aria-label={this.videoTitle}
                            role="button"
                        >
                            <ux-icon name="chevron-right" weight="normal" />
                        </a>
                    </div>
                </ux-iws-link-wrapper>
            </Host>
        );
    }
}
